import type {
  Action as ProcessAction,
  OwnerActionResponse,
} from "../types/action";
import { type Artifact, type InstagramPost } from "../types/artifact";
import type { PanelProcess } from "../types/state";

import { SCHEDULABLE_ARTIFACT_TYPES } from "./setupCardItems";

// Seeded "job" processes carry a deterministic id prefix and have no artifact
// child. Each prefix below routes to the panel that job owns. This list is
// deliberately a SUBSET of JOB_ID_PREFIXES in services/py/api/routes/processes.py:
// `campaign_job:` is not a job at all, and it must keep reaching the default
// "campaign" arm below, which is where its pause control lives. `chat_sweep_job:`
// never reaches this function, because the jobs filter hides it.
// The ids come from competitor_monitor_process_id(), daily_sms_process_id() and
// competitor_video_insights_process_id() in services/py/shared/agents/.
// A recurring campaign's id prefix. Exported because two UI surfaces test for
// it: the Jobs list, to open the job a campaign row stands for, and the
// Campaigns space, to drop what a `kind=campaign` request must never return.
// Mirrors CAMPAIGN_ID_PREFIX in services/py/api/routes/processes.py.
export const CAMPAIGN_ID_PREFIX = "campaign_job:";

const COMPETITOR_MONITOR_JOB_PREFIX = "competitor_monitor_job:";
const DAILY_SMS_JOB_PREFIX = "daily_sms_job:";
const COMPETITOR_VIDEO_INSIGHTS_JOB_PREFIX = "competitor_video_insights_job:";

// Artifact types that open the standalone monitor editor (MonitorProcessPanel).
// This is the single source of truth shared with MONITOR_PANEL_CONFIG in the UI:
// classifyProcessPanel returns "monitor" only for these, so "classified as a
// monitor" always means "has an editor" — the two cannot drift.
export const MONITOR_PANEL_TYPES = [
  "subreddit_monitor",
  "ig_comment_watcher_class",
  "custom_monitor",
] as const;

export type MonitorPanelType = (typeof MONITOR_PANEL_TYPES)[number];

const monitorPanelTypes = new Set<string>(MONITOR_PANEL_TYPES);

export function isMonitorPanelType(type: string): type is MonitorPanelType {
  return monitorPanelTypes.has(type);
}

export type ProcessPanelKind =
  | "competitor_monitor"
  | "daily_sms"
  | "competitor_video_insights"
  | "monitor"
  | "campaign";

// The structural minimum the child selectors below actually read. `PanelProcess`
// and `ProcessSetupSnapshot` both satisfy it, so widening to this costs their
// callers nothing — but it also admits a bare `GET /processes/{id}/children`
// response, which is neither of those and would otherwise need an
// `as unknown as PanelProcess` cast over a dozen fields it doesn't have.
export type ProcessChildrenView = {
  children?: Artifact[];
  actions?: ProcessAction[];
  skipped_artifact_ids?: string[];
  skipped_action_ids?: string[];
};

// One place that decides which panel a process opens. Two families plus campaign:
//  - seeded-job dashboards (competitor_monitor, daily_sms): read-only, identified
//    by a deterministic id prefix, no artifact child.
//  - monitor editors (monitor): one single monitor artifact with no schedulable
//    sibling; the editor is picked by the child's type via MONITOR_PANEL_CONFIG.
//  - campaign: everything else (multi-item dashboard; monitors here list under "Jobs").
export function classifyProcessPanel(process: PanelProcess): ProcessPanelKind {
  if (process.id.startsWith(COMPETITOR_MONITOR_JOB_PREFIX)) {
    return "competitor_monitor";
  }
  if (process.id.startsWith(DAILY_SMS_JOB_PREFIX)) {
    return "daily_sms";
  }
  if (process.id.startsWith(COMPETITOR_VIDEO_INSIGHTS_JOB_PREFIX)) {
    return "competitor_video_insights";
  }

  // Skipped artifacts never activate, so they must not influence classification —
  // a monitor whose only schedulable sibling was skipped is still a lone
  // monitor, not a campaign.
  const children = (process.children ?? []).filter(notSkipped(process));
  // Exactly one, because MonitorProcessPanel renders a single monitor. A
  // process holding two or more would show one of them and hide the rest, so it
  // falls through to the campaign dashboard, which lists every monitor under
  // "Jobs". The test is the COUNT, not the type: one rule covers a pair of
  // custom monitors and a pair of comment watchers alike. Reuses
  // getMonitorArtifacts (below) rather than re-filtering inline, so this count
  // and the Jobs list can never drift apart on what counts as a monitor child.
  const monitorEditorCount = getMonitorArtifacts(process).length;
  if (monitorEditorCount === 1) {
    const hasSchedulable = children.some((c) =>
      SCHEDULABLE_ARTIFACT_TYPES.has(c.type),
    );
    if (!hasSchedulable) return "monitor";
  }

  return "campaign";
}

// Artifacts the user skipped in setup stay in the process's children (the
// /children loader returns the full artifact_ids so skip is restorable) but must
// never render in the active-campaign view — they were never activated.
export function notSkipped(
  process: ProcessChildrenView,
): (artifact: Artifact) => boolean {
  const skipped = new Set(process.skipped_artifact_ids ?? []);
  return (artifact: Artifact) => !skipped.has(artifact.id);
}

export function getMonitorArtifacts(process: ProcessChildrenView): Artifact[] {
  return (process.children ?? [])
    .filter(notSkipped(process))
    .filter((c) => isMonitorPanelType(c.type));
}

/** The asks the owner performs off-platform. These are ACTIONS, not artifacts —
 * an ask is not content anyone reviews — so the artifact selectors will never
 * return them and the campaign panel needs its own. */
export function getOwnerActions(process: ProcessChildrenView): ProcessAction[] {
  return (process.actions ?? []).filter(
    (a) => a.type === "request_owner_action",
  );
}

export function isSkippedAction(
  process: PanelProcess,
  actionId: string,
): boolean {
  return (process.skipped_action_ids ?? []).includes(actionId);
}

/** The owner's current answer to an ask, or null if they have not reported back.
 *
 * The one reader of `outputs.response` — every surface goes through here so an
 * answer cannot look recorded on one screen and missing on another. It absorbs
 * two shapes on purpose: the array written now, and the bare object still held by
 * rows written before the array landed (in Cosmos, and duplicated in three places
 * in Redis user state), so no data migration is needed. Newest entry wins.
 *
 * Truthiness is NOT a substitute for this: `Boolean([])` is `true`, so a raw check
 * reports an unanswered ask as answered. */
export function latestOwnerActionResponse(
  action: ProcessAction,
): OwnerActionResponse | null {
  const stored = (action.outputs ?? {}).response;
  if (!stored) return null;
  if (!Array.isArray(stored)) return stored;
  return stored.length ? (stored[stored.length - 1] ?? null) : null;
}

/** Whether the owner has reported back on an ask. Keyed on outputs.response, not
 * on any status the activation cascade rewrites. */
export function isOwnerActionAnswered(action: ProcessAction): boolean {
  return latestOwnerActionResponse(action) !== null;
}

export function getContentArtifacts(process: ProcessChildrenView): {
  posts: InstagramPost[];
  emails: Artifact[];
} {
  const children = (process.children ?? []).filter(notSkipped(process));
  const posts = children.filter(
    (c): c is InstagramPost => c.type === "instagram_post",
  );
  const emails = children.filter(
    (c) => c.type === "email_html" || c.type === "email_plaintext",
  );
  return { posts, emails };
}
